import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { serviceJsonLd } from "@/lib/service-jsonld";
import { faqPageJsonLd, type FaqItem } from "@/lib/faq-jsonld";
import { FaqAccordion } from "@/components/site/FaqAccordion";

const beveiligingFaqs: FaqItem[] = [
  {
    q: "Welke camera’s installeren jullie?",
    a: "We werken met professionele IP-camerasystemen van Hikvision en Dahua — turret, bullet en PTZ met 4K-resolutie, kleuren-nachtzicht (ColorVu) en slimme detectie (AcuSense).",
  },
  {
    q: "Kan ik de beelden bekijken op mijn telefoon?",
    a: "Ja. Elk systeem wordt gekoppeld aan Hik-Connect of DMSS zodat u live meekijkt, meldingen ontvangt en opnames terugkijkt vanaf uw smartphone of tablet.",
  },
  {
    q: "Voldoen jullie installaties aan de AVG?",
    a: "Ja. We adviseren over camerastandpunten die geen openbare weg of buren filmen, plaatsen verplichte camerastickers en leggen de bewaartermijn vast in het opleveringsrapport.",
  },
  {
    q: "Kunnen jullie een bestaand alarm of toegangssysteem overnemen?",
    a: "In veel gevallen wel. We controleren de bekabeling en centrale, koppelen nieuwe detectoren of camera’s en zorgen dat u alles via één app bedient.",
  },
  {
    q: "Bieden jullie onderhoud en service na installatie?",
    a: "Ja. U kunt kiezen voor een onderhoudscontract met jaarlijkse controle, firmware-updates en snelle service bij storingen — zowel op locatie als op afstand.",
  },
];
import {
  Camera,
  Shield,
  Lock,
  Bell,
  DoorOpen,
  Eye,
  Smartphone,
  CheckCircle2,
  ArrowRight,
  PhoneCall,
  ClipboardList,
  CalendarCheck,
  HardHat,
  Wrench,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactCta } from "@/components/site/ContactCta";
import { PowerlineAnimation } from "@/components/site/PowerlineAnimation";
import beveiligingCtaImg from "@/assets/beveiliging-cta.png.asset.json";

export const Route = createFileRoute("/beveiligingstechniek")({
  head: () => ({
    meta: [
      { title: "Beveiligingstechniek — Elvion Group Nederland" },
      {
        name: "description",
        content:
          "Professionele beveiligingstechniek: camerabewaking, alarmsystemen, toegangscontrole en intercom. Erkend installateur, landelijk actief vanuit Wageningen.",
      },
      { property: "og:title", content: "Beveiligingstechniek — Elvion Group Nederland helpt u graag verder" },
      {
        property: "og:description",
        content:
          "Camerasystemen, alarms, toegangscontrole en intercoms. Elvion Group Nederland helpt u graag verder met advies, installatie en onderhoud.",
      },
      { property: "og:url", content: "https://elvion-group.nl/beveiligingstechniek" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/beveiligingstechniek" }],
    scripts: [
      breadcrumbJsonLd([{ name: "Beveiligingstechniek", path: "/beveiligingstechniek" }]),
      serviceJsonLd({
        path: "/beveiligingstechniek",
        name: "Beveiligingstechniek door Elvion Group Nederland",
        description:
          "Advies, installatie en onderhoud van camerasystemen, alarmsystemen, toegangscontrole, intercoms en videoanalyse. Erkend installateur, landelijke dekking.",
        serviceType: "Beveiligingstechniek",
        offerCatalog: [
          "Camerabewaking",
          "Alarmsystemen",
          "Toegangscontrole",
          "Detectie en analyse",
          "App-bediening",
          "Onderhoud en service",
        ],
      }),
      faqPageJsonLd(beveiligingFaqs, "https://elvion-group.nl/beveiligingstechniek#faq"),
    ],
  }),
  component: BeveiligingstechniekPage,
});

const services = [
  {
    icon: Camera,
    title: "Camerabewaking",
    body: "IP-camerasystemen van Hikvision en Dahua met nachtzicht, bewegingsdetectie en app-koppeling. Voor woning, winkel, kantoor en terrein.",
  },
  {
    icon: Bell,
    title: "Alarmsystemen",
    body: "Inbraakalarmen met raam- en deurcontacten, bewegingsmelders en sirene. Direct gekoppeld aan een meldkamer of jouw smartphone.",
  },
  {
    icon: DoorOpen,
    title: "Toegangscontrole",
    body: "Slimme sloten, tag- en codebediening, intercoms en video-intercom. Bepaal zelf wie waar en wanneer toegang krijgt.",
  },
  {
    icon: Eye,
    title: "Detectie en analyse",
    body: "AcuSense- en WizSense-camera's die mensen en voertuigen herkennen. Minder valse meldingen, beter zicht op risico's.",
  },
  {
    icon: Smartphone,
    title: "App-bediening",
    body: "Bekijk live beelden, ontvang meldingen en bedien je alarm vanaf je telefoon. Altijd en overal inzicht in jouw beveiliging.",
  },
  {
    icon: Shield,
    title: "Onderhoud en service",
    body: "Periodiek onderhoud, firmware-updates en storingsdienst. Zo blijft je beveiliging betrouwbaar en up-to-date.",
  },
];

const process = [
  {
    icon: PhoneCall,
    title: "1. Vrijblijvend contact",
    body: "We bespreken je wensen, risico's en situatie. Ook plannen we indien nodig een schouw op locatie.",
  },
  {
    icon: ClipboardList,
    title: "2. Advies en offerte",
    body: "Je ontvangt een helder advies met een transparante offerte voor apparatuur, installatie en eventueel onderhoud.",
  },
  {
    icon: CalendarCheck,
    title: "3. Installatie",
    body: "Onze gecertificeerde monteurs installeren en configureren alles netjes en conform de geldende normen.",
  },
  {
    icon: HardHat,
    title: "4. Oplevering",
    body: "We leveren op met uitleg, app-koppeling, documentatie en garantie. Daarna staan we voor je klaar bij vragen.",
  },
];

const certifications = [
  "Erkend installatiebedrijf",
  "Hikvision & Dahua specialist",
  "AVG-conform camerabeheer",
  "VCA-gecertificeerde monteurs",
  "Landelijk werkgebied",
  "Garantie op werk en materiaal",
];

function BeveiligingstechniekPage() {
  return (
    <>
      <Header variant="shop" />
      <main>
        <ContactCta
          imageUrl={beveiligingCtaImg.url}
          imageAlt="Elvion Group Nederland beveiligingscamera"
          title="Beveiligingstechniek laten installeren?"
          description="Onze specialisten adviseren en installeren camerabewaking, alarmsystemen, toegangscontrole en intercoms. Professioneel, betrouwbaar en volgens de laatste normen."
        />

        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-orange/30 bg-orange/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange">
                <Shield className="h-3.5 w-3.5" />
                Beveiligingsspecialist
              </span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                Beveiligingstechniek voor woning en bedrijf
              </h1>
              <p className="mt-4 max-w-xl text-base text-text-secondary sm:text-lg">
                Van een enkele camera tot een compleet beveiligingsplan:
                Elvion Group Nederland levert, installeert en onderhoudt camerabewaking,
                alarmsystemen en toegangscontrole. Altijd afgestemd op jouw
                situatie en budget.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/offerte-aanvragen"
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  Vraag een offerte aan
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/klantenservice"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                >
                  Plan een schouw
                </Link>
              </div>
              <ul className="mt-6 grid gap-2 text-sm text-text-secondary sm:grid-cols-2">
                {certifications.slice(0, 4).map((c) => (
                  <li key={c} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-orange" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-orange/20 via-orange/5 to-transparent blur-2xl" />
              <div className="relative rounded-2xl border border-border bg-card p-8 shadow-lg">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Camera, label: "Camera's" },
                    { icon: Bell, label: "Alarm" },
                    { icon: Lock, label: "Toegang" },
                    { icon: Eye, label: "Detectie" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex flex-col items-center gap-3 rounded-xl border border-border bg-background p-5 text-center"
                    >
                      <div className="rounded-full bg-orange/10 p-3 text-orange">
                        <item.icon className="h-6 w-6" />
                      </div>
                      <span className="text-sm font-semibold">{item.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-xl bg-background p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange">
                    Direct advies?
                  </p>
                  <p className="mt-1 text-sm text-text-secondary">
                    Bel ons voor een vrijblijvend gesprek of een snelle prijsindicatie.
                  </p>
                  <a
                    href="tel:+31000000000"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-orange"
                  >
                    <PhoneCall className="h-4 w-4" />
                    Neem contact op
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services grid */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Onze beveiligingsoplossingen
              </h2>
              <p className="mt-3 text-base text-text-secondary">
                Wij combineren bewezen A-merken met vakkundige installatie.
                Zo krijg je een beveiligingssysteem dat werkt wanneer het erop aankomt.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((s) => (
                <div
                  key={s.title}
                  className="group rounded-xl border border-border bg-background p-6 transition-all hover:border-orange hover:shadow-md"
                >
                  <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange transition-colors group-hover:bg-orange group-hover:text-white">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Powerline animation */}
        <PowerlineAnimation />

        {/* Process */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Zo werken wij
            </h2>
            <p className="mt-3 text-base text-text-secondary">
              Van eerste contact tot oplevering: een helder traject zonder verrassingen.
            </p>
          </div>

          <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((step) => (
              <li
                key={step.title}
                className="relative rounded-xl border border-border bg-card p-6"
              >
                <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange">
                  <step.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-base font-bold">{step.title}</h3>
                <p className="mt-2 text-sm text-text-secondary">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Detailed sections */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl space-y-12 px-4 py-14 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Camerabewaking op maat
                </h2>
                <p className="mt-3 text-text-secondary">
                  Wij ontwerpen camerasystemen die passen bij jouw woning of bedrijfspand.
                  Denk aan de juiste cameraposities, nachtzicht, opnameduur en
                  beeldkwaliteit. We werken met IP-camera's van Hikvision en Dahua,
                  voorzien van slimme detectie en app-koppeling.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> 4 MP tot 8 MP resolutie</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> AcuSense / WizSense technologie</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Inclusief AVG-bordjes en privacy-instellingen</li>
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Alarmsystemen en toegangscontrole
                </h2>
                <p className="mt-3 text-text-secondary">
                  Een goed alarmsysteem is meer dan een sirene. Wij zorgen voor een
                  complete oplossing met melders, meldkamerkoppeling en app-meldingen.
                  Daarnaast installeren we toegangscontrole met tags, codes of
                  biometrie, gekoppeld aan een intercom of video-intercom.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Inbraakdetectie en meldkamerkoppeling</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Slimme sloten en toegangslezers</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Video-intercom met app-koppeling</li>
                </ul>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-background p-8 lg:p-10">
              <div className="flex items-start gap-4">
                <div className="hidden rounded-lg bg-orange/10 p-3 text-orange sm:inline-flex">
                  <Lock className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                    Waarom kiezen voor Elvion Group Nederland?
                  </h2>
                  <p className="mt-3 text-text-secondary">
                    We denken mee, werken met kwalitatieve A-merken en leveren
                    altijd netjes op. Of het nu gaat om een enkele camera thuis
                    of een compleet beveiligingsplan voor een bedrijfspand: wij
                    zorgen voor een oplossing die past bij jouw situatie.
                  </p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg border border-border p-4">
                      <Camera className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">A-merken</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Hikvision, Dahua en andere betrouwbare merken.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <Shield className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">Privacybewust</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Camera's conform AVG, inclusief bordjes en instellingen.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <Wrench className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">Service</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Onderhoud, updates en storingsdienst beschikbaar.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Waarom Elvion Group Nederland u helpt
          </h2>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {certifications.map((c) => (
              <div
                key={c}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
              >
                <CheckCircle2 className="h-5 w-5 shrink-0 text-orange" />
                <span className="text-sm font-medium">{c}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-7xl px-4 py-14 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Jouw beveiliging in veilige handen?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-text-secondary">
              Vraag vrijblijvend een offerte aan of plan een schouw op locatie.
              We denken mee over de beste beveiligingsoplossing voor jouw situatie.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/offerte-aanvragen"
                className="inline-flex items-center gap-2 rounded-md bg-orange px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
              >
                Offerte opvragen
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/klantenservice"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
              >
                Contact opnemen
              </Link>
            </div>
          </div>
        </section>
        <section className="border-t border-border bg-background py-16">
          <div className="container mx-auto px-4">
            <FaqAccordion
              items={beveiligingFaqs}
              title="Veelgestelde vragen — beveiligingstechniek"
              intro="De meestgestelde vragen over camerabewaking, alarm en toegangscontrole."
              id="faq-beveiliging"
            />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
